/**
 * STICK SHAPING — the one genuine *choice* in AD-007n, and the reason it is an
 * input option rather than a flight-model tier.
 *
 * `RB.EXE`'s keyboard control is a ramped analogue axis: held, the aileron takes
 * about two seconds to reach full deflection; released, it keeps rolling for
 * about another second (`packages/engine`'s `stick.ts` has the measurements).
 * The port's keyboard was bang-bang, so every input was a full-deflection input
 * and a player who wanted a gentle turn had no way to ask for one.
 *
 * **Authentic is the default.** It is what the original does *and* it is what
 * makes a keyboard proportional, which is the half that matters for feel.
 * **Instant** is kept because it is a defensible modern expectation and because
 * it is what the port shipped, so nobody's muscle memory is taken away without
 * a way back.
 *
 * It is deliberately **not** on the Realism Panel and deliberately **not** part
 * of NOVICE / INTERMEDIATE / EXPERT. Those three are the original's own
 * flight-model ladder (AD-007b) and are already spoken for; this is a property
 * of the input device, not of the aeroplane, which is also why an analogue axis
 * would bypass it entirely. Same storage pattern as `effects-settings.ts` and
 * `cockpit-settings.ts`: a small `SettingsStore` round-trip, storage-agnostic so
 * it is testable without a real `localStorage`.
 */

import type { StickShapingId } from "@red-opus/engine";
import type { SettingsStore } from "../audio/settings.ts";

export const STICK_SETTINGS_STORAGE_KEY = "red-opus/stick-settings";

export interface StickSettings {
  readonly shaping: StickShapingId;
}

export const DEFAULT_STICK_SETTINGS: StickSettings = { shaping: "authentic" };

/** Reads whatever survives a `JSON.parse` of untrusted stored text, falling back to the default. */
export function normalizeStickSettings(candidate: unknown): StickSettings {
  if (typeof candidate !== "object" || candidate === null) return DEFAULT_STICK_SETTINGS;
  const raw = candidate as Partial<Record<keyof StickSettings, unknown>>;
  return { shaping: raw.shaping === "instant" ? "instant" : "authentic" };
}

export function serializeStickSettings(settings: StickSettings): string {
  return JSON.stringify(settings);
}

/** Defaults on missing key, corrupt JSON, or a non-object payload — never throws. */
export function loadStickSettings(store: SettingsStore): StickSettings {
  const raw = store.getItem(STICK_SETTINGS_STORAGE_KEY);
  if (raw === null) return DEFAULT_STICK_SETTINGS;
  try {
    return normalizeStickSettings(JSON.parse(raw));
  } catch {
    return DEFAULT_STICK_SETTINGS;
  }
}

export function saveStickSettings(store: SettingsStore, settings: StickSettings): void {
  store.setItem(STICK_SETTINGS_STORAGE_KEY, serializeStickSettings(settings));
}
